import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { css, Theme } from '@emotion/react';
import {
  stroot,
  organisation as organisationAPI,
  organisationUsers,
} from '@birdi/js-sdk';
import { useMetaSelector } from '../../meta/reducer';
import { buttonOutlineBlack, breakpoints, BirdiTheme } from '@birdi/theme';
import { FeatureFlagNames } from '@birdi/js-sdk/src/public/organisation-features';
import { blocks } from '@birdi/theme';
import { AddWorkspaceModal } from '../../user-workspaces/add-workspace-modal';
import { DeleteWorkspaceModal } from '../../user-workspaces/delete-workspace-modal';
import { AddUserToWorkspaceModal } from '../../user-workspaces/add-user-to-workspace-modal';
import { WorkspaceTable } from './workspace-table';
import { WorkspacesBadge } from './workspaces-badge';

const headerButtonStyle = (theme: BirdiTheme) => css`
  ${buttonOutlineBlack(theme)};
  background-color: ${theme.mono0};
  border: 1px solid ${theme.mono40};
  border-radius: 4px;
  height: 2em;
  span {
    color: ${theme.mono50};
  }
`;

export const Workspaces = (): React.ReactElement => {
  const meta = useMetaSelector();

  const [details, setDetails] = useState(null);

  const [showAddWorkspaceModal, setShowAddWorkspaceModal] = useState(false);
  const [showAddUserToWorkspaceModal, setShowAddUserToWorkspaceModal] =
    useState(false);
  const [workspacesList, setWorkspacesList] = useState([]);

  const [showDeleteWorkspaceModal, setShowDeleteWorkspaceModal] =
    useState(false);
  const [deletingWorkspaceId, setDeletingWorkspaceId] = useState<string | null>(
    null,
  );

  const canCreateWorkspace =
    meta.permissions.includes('organisation:workspace:create') &&
    meta.features.includes('add-workspaces');
  const isWorkspaceAdmin = meta.permissions.includes('workspace:user:create');
  const workspacesLimit = meta.organisation.planData?.workspacesLimit;

  const isPrimaryOrg = !meta.workspace;

  const workspacesEnable =
    meta.features.includes('add-workspaces') && isPrimaryOrg;

  const createWorkspaceEnable = canCreateWorkspace && workspacesEnable;
  const editWorkspaceUsersEnable = isWorkspaceAdmin && workspacesEnable;

  const navigate = useNavigate();
  const location = useLocation();

  const { pathname } = location;
  let scopeId = null;
  if (pathname.includes('profile-workspaces')) {
    scopeId = pathname.substring(pathname.lastIndexOf('/') + 1);
  }

  const addNewWorkspace = () => {
    setShowAddWorkspaceModal(true);
  };

  const deleteWorkspace = (id: string) => {
    setDeletingWorkspaceId(id);
    setShowDeleteWorkspaceModal(true);
  };

  const adduserToWorkspace = () => {
    setShowAddUserToWorkspaceModal(true);
  };

  // console.log('details: ', details);

  const deleteWorkspaceForOneUser = async (workspaceId: string) => {
    if (scopeId && details) {
      const confirmed = window.confirm(
        'Are you sure you want to delete this workspace?',
      );
      if (!confirmed) return;

      // need to check if we are currently in this user scope
      const deletingUser = await organisationAPI.getScopeIdForDeletingUser(
        stroot('soh0eh'),
        workspaceId,
        details.id,
      );

      if (deletingUser.body.scopeId === scopeId) {
        await organisationAPI.deleteWorkspaceFromUser(
          stroot('xa5up8'),
          workspaceId,
          details.id,
        );
        navigate('/');
      } else {
        await organisationAPI.deleteWorkspaceFromUser(
          stroot('xa5up8'),
          workspaceId,
          details.id,
        );
        window.location.reload();
      }
    }
  };

  useEffect(() => {
    if (scopeId) {
      organisationUsers
        .getUserDetails(stroot('as7oo7'), scopeId)
        .then((res) => setDetails(res.body));
    }
    organisationAPI.getWorkspaces(stroot('xon5oo'), scopeId).then((res) => {
      setWorkspacesList(res.body.items);
    });
  }, []);

  if (scopeId && !details) return <div />;

  return (
    <div>
      <div css={blocks.pageTitleHolder}>
        <h2 css={blocks.pageTitle}>
          {scopeId ? `${details.fname} ${details.lname}` : 'Workspaces'}
        </h2>
      </div>
      <div
        css={(theme) => css`
          ${blocks.pageGraySection(theme)};
          justify-content: space-between;
          @media (max-width: ${breakpoints.m}) {
            padding-bottom: 1em;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            @media (max-width: ${breakpoints.m}) {
              flex-direction: column;
              justify-content: flex-start;
              align-items: flex-start;
              p {
                margin-bottom: 0;
              }
            }
          `}
        >
          <p
            css={css`
              margin-right: 0.5em;
            `}
          >
            {!scopeId
              ? 'These are all the workspaces in your organization.'
              : 'Manage users workspaces.'}
          </p>
          <div
            css={css`
              margin: 1em;
            `}
          >
            <WorkspacesBadge
              workspacesUsed={workspacesList.length}
              workspacesLimit={workspacesLimit}
            />
          </div>
        </div>
        {createWorkspaceEnable && !scopeId && (
          <button
            type="button"
            css={headerButtonStyle}
            onClick={addNewWorkspace}
          >
            <span>+ Create a workspace</span>
          </button>
        )}
        {editWorkspaceUsersEnable && scopeId && (
          <button
            type="button"
            css={headerButtonStyle}
            onClick={adduserToWorkspace}
          >
            <span>+ Add workspace</span>
          </button>
        )}
      </div>
      {workspacesList.length === 0 ? (
        <div
          css={css`
            padding: 2em;
          `}
        >
          No workspaces found ...
        </div>
      ) : (
        <section
          css={css`
            padding-bottom: 2em;
          `}
        >
          <WorkspaceTable
            workspaces={workspacesList}
            deleteWorkspace={deleteWorkspace}
            scopeId={scopeId}
            isCreator={details && details.roleName === 'Organisation Admin'}
            deleteWorkspaceForOneUser={deleteWorkspaceForOneUser}
            createWorkspaceEnable={createWorkspaceEnable}
            editWorkspaceUsersEnable={editWorkspaceUsersEnable}
          />
        </section>
      )}
      {createWorkspaceEnable && (
        <div>
          <AddWorkspaceModal
            open={showAddWorkspaceModal}
            setOpen={setShowAddWorkspaceModal}
            switchToWS={false}
          />
        </div>
      )}
      {editWorkspaceUsersEnable && deletingWorkspaceId && (
        <DeleteWorkspaceModal
          open={showDeleteWorkspaceModal}
          setOpen={(status) => {
            setShowDeleteWorkspaceModal(status);
            if (!status) setDeletingWorkspaceId(null);
          }}
          id={deletingWorkspaceId}
        />
      )}
      {editWorkspaceUsersEnable && workspacesList.length !== 0 && (
        <AddUserToWorkspaceModal
          open={showAddUserToWorkspaceModal}
          setOpen={setShowAddUserToWorkspaceModal}
          scopeId={scopeId}
          existingWorkspacesIds={workspacesList.map(
            (workspace) => workspace.id,
          )}
        />
      )}
    </div>
  );
};
